// modalChrome — デスクトップ (中央固定) / スマホ (フルスクリーン) を切り替える
// モーダル枠クラスの共通ヘルパ。
//
// もともと TimelapseShareModal 内に閉じていたが、過去キャンバス一覧モーダル
// (past-canvas-new-document-2026-07-05.md §7 Slice 2) が**同じ視覚言語**で
// 開くため共通ファイルに切り出した (consult 2026-07-09)。TimelapseShareModal は
// 後方互換のため本モジュールを import しつつ同名で re-export する
// (既存 TimelapseShareModal.test.ts の import を壊さない)。
//
// 純粋なクラス文字列生成のみ (DOM/React 非依存 = node vitest でそのままテスト可能)。

// スマホ時のモーダル本体 (スクロール body) の水平インセット。full-width の水平ドラッグ
// コントロールの操作トラックを物理画面端から ~32px 以上離し、Android のエッジスワイプ
// 「戻る」(= OS のシステムジェスチャ。画面端の touch を web に渡す前に横取りするので
// touch-action/overscroll/preventDefault では止められない、oekaki CLAUDE.md「スマホの
// 画面端スワイプ『戻る』」節) の誤発動を抑止する。前例: ModernPhoneShell の
// SHEET_EDGE_INSET_CLASS (= 'px-6'、safe-area+border 込みで実測 ~33px ≥ ジェスチャゾーン
// ~24dp)。デスクトップ (420px センター、端に触れない) には付けない = 狭めない。
export const PHONE_BODY_EDGE_INSET_CLASS = 'px-6' as const;

/** モーダル外枠 (root) / 本体 (dialog) / ヘッダ / 本文スクロール領域のクラス束。 */
export interface TimelapseModalChrome {
  readonly root: string;
  readonly dialog: string;
  readonly header: string;
  readonly body: string;
}

/**
 * スマホ / デスクトップでモーダルの枠組みクラスを切り替える純関数 (node テスト可能)。
 *   - デスクトップ: 従来の中央 420px センターモーダル (完全不変)。
 *   - スマホ: `inset-0` フルスクリーン + 縦 flex (ヘッダ sticky-top / 本文 flex-1 /
 *     フッターは各ステップ View 側で sticky-bottom)。safe-area を上部 padding と
 *     フッターの下部 padding で吸収し、`overflow-x-hidden` で横あふれを封じる。
 */
export const timelapseModalChrome = (isMobile: boolean): TimelapseModalChrome =>
  isMobile
    ? {
        root: 'fixed inset-0 z-50',
        dialog:
          'flex h-[100dvh] w-full flex-col overflow-y-auto overflow-x-hidden ' +
          'border border-white/10 bg-[var(--color-panel)] shadow-xl ' +
          '[padding-top:env(safe-area-inset-top)]',
        header:
          'sticky top-0 z-10 flex items-center justify-between gap-2 ' +
          'border-white/10 border-b bg-[var(--color-panel)] px-4 py-3',
        body: `flex-1 ${PHONE_BODY_EDGE_INSET_CLASS} pb-4`,
      }
    : {
        // `scrollbar-gutter:stable` = スクロールバー分の溝を常時確保し、出現/消滅で
        // 内側幅が変わらないようにする。固定幅 420px + `overflow-y-auto` + カードの
        // `aspect-square` (幅→高さ連動) の組合せだと、スクロールバー出現で内側が
        // 狭まる→サムネ高が縮む→合計高が減ってバーが消える→幅が戻る…の発振が起き、
        // 中央寄せ (place-items-center) でダイアログが上下にビクつく (2026-07-13 実機報告)。
        root: 'fixed inset-0 z-50 grid place-items-center p-4',
        dialog:
          'max-h-[90vh] w-[420px] max-w-full overflow-y-auto [scrollbar-gutter:stable] rounded ' +
          'border border-white/10 bg-[var(--color-panel)] p-5 shadow-xl',
        header: 'mb-4 flex items-center justify-between',
        body: '',
      };

/**
 * ステップ View の主ボタン行クラス。スマホ時は本文スクロールと独立に画面下部へ
 * 常時固定 (`sticky bottom-0`、`-mx-6` で本文の水平インセット `px-6`
 * (PHONE_BODY_EDGE_INSET_CLASS) を打ち消しフルブリード、下部は safe-area 吸収)。
 * 自身の `px-6` でボタンも画面端から離す (body と同じインセット量)。デスクトップは
 * 従来の右寄せ行 (完全不変)。
 */
export const timelapseFooterRowClass = (isMobile: boolean): string =>
  isMobile
    ? 'sticky bottom-0 z-10 -mx-6 mt-1 flex justify-end gap-2 border-white/10 border-t ' +
      'bg-[var(--color-panel)] px-6 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]'
    : 'flex justify-end gap-2';
